import { ChangeEvent, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useQueryClient } from 'react-query'
import { useCreateAccess } from '../../api/stage'
import Account from '../../types/account'
import StageAccess from '../../types/stage-access'
import StageAccessRow from './StageAccessRow'
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Stack,
  TextField,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import Button from '@mui/material/Button'
import Checkbox from '../common/Checkbox'

interface Props {
  pid?: string
  id?: string
  title?: string
  accounts?: Account[]
  accesses: StageAccess[]
}

export default function StageAccessCard({
  pid = '',
  id = '',
  title = '',
  accounts,
  accesses,
}: Props) {
  const createAccess = useCreateAccess()
  const queryClient = useQueryClient()
  const [canRead, setCanRead] = useState<boolean>(true)
  const [canWrite, setCanWrite] = useState<boolean>(true)
  const [selectedAccounts, setSelectedAccounts] = useState<string>('')
  const options = useMemo(
    () =>
      accounts
        ?.filter((account) =>
          accesses.every((access) => access.account.id !== account.id)
        )
        ?.map((account) => ({
          id: account.id,
          label: account.fullName,
        })),
    [accounts, selectedAccounts]
  )

  async function submitRequest() {
    if (selectedAccounts !== '')
      await createAccess.mutateAsync(
        {
          accounts: [selectedAccounts],
          read: canRead,
          write: canWrite,
          stageId: id,
        },
        {
          onSuccess: () => {
            toast.success('دسترسی جدید اعطا شد.')
            queryClient.invalidateQueries(`accesses-${pid}`)
            setSelectedAccounts('')
          },
        }
      )
  }

  return (
    <Card>
      <CardHeader title={title} sx={{ textAlign: 'center' }} />
      <CardContent>
        <Grid container gap={{ xs: 1, md: 3 }} sx={{ px: 1 }}>
          <Grid item xs={12} md={6}>
            <Autocomplete
              renderInput={(params) => <TextField {...params} label=" افزودن کاربر" />}
              className="access-profile-select"
              onChange={(e: any, item) => setSelectedAccounts(item?.id ?? '')}
              options={options ?? []}
              noOptionsText={<div>کاربر ساده بدون دسترسی وجود ندارد</div>}
            />
          </Grid>
          {selectedAccounts !== '' && (
            <Stack
              direction="row"
              flexGrow={1}
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" gap={1} alignItems="center">
                <Checkbox
                  label="تغییر"
                  value={canWrite}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setCanWrite(e.target.checked)
                  }
                />
                <Checkbox
                  label="خواندن"
                  value={canRead}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setCanRead(e.target.checked)
                  }
                />
              </Stack>
              <Box>
                <Button
                  variant="text"
                  className="grant-access-button"
                  onClick={submitRequest}
                  endIcon={<AddIcon color="primary" />}
                >
                  افزودن
                </Button>
              </Box>
            </Stack>
          )}
        </Grid>

        <div className="access-table max-h-64 overflow-auto">
          {accesses.length > 0 ? (
            <table className="mt-4 w-full table-fixed">
              <colgroup>
                <col span={1} className="w-1/12" />
                <col span={1} />
                <col span={1} className="w-1/6" />
                <col span={1} className="w-1/6" />
              </colgroup>
              <thead className="h-12">
                <tr>
                  <th className="text-right"></th>
                  <th className="text-right">کاربر</th>
                  <th className="text-xs">مشاهده</th>
                  <th className="text-xs">تغییر</th>
                </tr>
              </thead>
              <tbody>
                {accesses.map((access) => (
                  <StageAccessRow
                    pid={pid}
                    id={access.id}
                    read={access.read}
                    write={access.write}
                    account={access.account}
                    key={access.id}
                  />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-xs text-slate-600 text-center p-7">
              فقط ادمین‌ها به این مرحله دسترسی دارند
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
